<template>
  <div class="card scan-folheto-card">
    <div class="card-header pb-0">
      <h6 class="mb-0">Novo Scan de Folheto</h6>
      <p class="text-sm text-secondary mb-0">Envie a imagem do folheto para cadastro</p>
    </div>

    <div class="card-body">
      <!-- Upload de imagem -->
      <label class="form-label-custom">
        <i class="fas fa-image me-1"></i>Imagem do Folheto
      </label>
      <div
        class="upload-zone"
        :class="{ 'drag-over': isDragging, 'has-file': selectedFile }"
        @click="triggerFileInput"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
      >
        <template v-if="!selectedFile">
          <div class="upload-placeholder">
            <div class="upload-icon-wrapper">
              <i class="fas fa-cloud-upload-alt"></i>
            </div>
            <p class="mb-1 text-sm font-weight-bold">Arraste ou clique para selecionar</p>
            <p class="mb-0 text-xs text-secondary">PNG, JPG, JPEG, WEBP</p>
          </div>
        </template>
        <template v-else>
          <div class="upload-preview-row">
            <img :src="previewUrl" alt="Preview do folheto" class="preview-thumb" />
            <div class="preview-meta">
              <i class="fas fa-check-circle text-success me-1"></i>
              <span class="text-sm text-truncate">{{ selectedFile.name }}</span>
            </div>
            <button class="btn-remove-file" @click.stop="clearFile" title="Remover imagem">
              <i class="fas fa-times-circle"></i>
            </button>
          </div>
        </template>
      </div>
      <input ref="fileInput" type="file" accept="image/*" class="d-none" @change="handleFileChange" />

      <!-- Erro -->
      <div v-if="scanError" class="alert alert-danger py-2 mt-3 mb-0">
        <i class="fas fa-exclamation-circle me-2"></i>{{ scanError }}
      </div>

      <!-- Botão escanear -->
      <div class="d-flex justify-content-end mt-3">
        <button
          class="btn btn-custom-gradient btn-scan"
          :disabled="!selectedFile || scanning"
          @click="openSupermarketModal"
        >
          <span v-if="scanning" class="d-flex align-items-center gap-2">
            <span class="spinner-border spinner-border-sm"></span>
            Enviando folheto...
          </span>
          <span v-else class="d-flex align-items-center gap-2">
            <i class="fas fa-search"></i>
            Escanear Folheto
          </span>
        </button>
      </div>
    </div>

    <!-- Modal de Seleção de Supermercados -->
    <div v-if="showSupermarketModal" class="modal-backdrop fade show" @click="closeSupermarketModal" style="z-index: 9999;"></div>
    <div v-if="showSupermarketModal" class="modal fade show d-block" tabindex="-1" aria-labelledby="selectSupermarketsModalLabel" aria-hidden="true" style="z-index: 10000;">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="selectSupermarketsModalLabel">
              <i class="fas fa-store me-2"></i>
              Selecionar Supermercados
            </h5>
            <button type="button" class="btn-close" @click="closeSupermarketModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="text-sm text-secondary mb-3">
              Selecione a quais supermercados este folheto pertence.
            </p>
            <div v-if="loadingSupermercados" class="text-center py-4">
              <div class="spinner-border spinner-border-sm spinner-teal" role="status"></div>
              <p class="mt-2 text-sm text-secondary">Carregando supermercados...</p>
            </div>
            <div v-else-if="supermarketError" class="alert alert-danger py-2 mb-0">
              {{ supermarketError }}
            </div>
            <div v-else-if="supermercados.length === 0" class="text-center text-secondary py-3">
              Nenhum supermercado encontrado
            </div>
            <div v-else class="supermarket-select-list">
              <label
                v-for="s in supermercados"
                :key="s.id"
                class="supermarket-select-row"
                :class="{ 'supermarket-select-row--selected': selectedSupermarketIds.has(s.id) }"
              >
                <input
                  type="checkbox"
                  class="supermarket-select-checkbox"
                  :checked="selectedSupermarketIds.has(s.id)"
                  @change="toggleSupermarket(s.id)"
                />
                <span>{{ s.nome || s.name }}</span>
              </label>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeSupermarketModal" :disabled="scanning">Cancelar</button>
            <button
              type="button"
              class="btn btn-custom-gradient"
              @click="confirmScan"
              :disabled="selectedSupermarketIds.size === 0 || scanning"
            >
              <span v-if="scanning">
                <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                Enviando...
              </span>
              <span v-else>
                <i class="fas fa-search me-1"></i>
                Escanear
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiRequest, handleApiResponse, API_ENDPOINTS } from '@/config/api.js';
import { showSuccessAlert, showErrorAlert } from '@/utils/swal.js';

export default {
  name: 'NovoScanFolhetoTable',

  data() {
    return {
      selectedFile: null,
      previewUrl: null,
      isDragging: false,
      scanning: false,
      scanError: null,
      showSupermarketModal: false,
      supermercados: [],
      loadingSupermercados: false,
      supermarketError: null,
      selectedSupermarketIds: new Set(),
    };
  },

  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) this.setFile(file);
    },

    handleDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) this.setFile(file);
    },

    setFile(file) {
      this.selectedFile = file;
      this.previewUrl = URL.createObjectURL(file);
      this.scanError = null;
    },

    clearFile() {
      this.selectedFile = null;
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
      this.$refs.fileInput.value = '';
    },

    async openSupermarketModal() {
      if (!this.selectedFile) return;
      this.showSupermarketModal = true;
      this.selectedSupermarketIds = new Set();

      if (this.supermercados.length === 0) {
        this.loadingSupermercados = true;
        this.supermarketError = null;
        try {
          const response = await apiRequest(API_ENDPOINTS.SUPERMARKET, { method: 'GET' });
          const data = await handleApiResponse(response);
          this.supermercados = Array.isArray(data) ? data : (data.data || data.supermercados || []);
        } catch (err) {
          console.error('Erro ao carregar supermercados:', err);
          this.supermarketError = err.message || 'Erro ao carregar supermercados';
        } finally {
          this.loadingSupermercados = false;
        }
      }
    },

    closeSupermarketModal() {
      if (this.scanning) return;
      this.showSupermarketModal = false;
    },

    toggleSupermarket(id) {
      const next = new Set(this.selectedSupermarketIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      this.selectedSupermarketIds = next;
    },

    async confirmScan() {
      if (this.selectedSupermarketIds.size === 0) return;
      await this.scanFolheto();
      if (!this.scanError) {
        this.showSupermarketModal = false;
      }
    },

    async scanFolheto() {
      if (!this.selectedFile) return;

      this.scanning = true;
      this.scanError = null;

      try {
        const formData = new FormData();
        formData.append('image', this.selectedFile);
        this.selectedSupermarketIds.forEach((id) => {
          formData.append('id_supermarkets[]', id);
        });

        const response = await apiRequest(API_ENDPOINTS.FLYER_SCAN, {
          method: 'POST',
          body: formData,
        });
        await handleApiResponse(response);

        await showSuccessAlert('Scan concluído!', 'O folheto foi enviado e está sendo processado.');
        this.clearFile();
        this.selectedSupermarketIds = new Set();
      } catch (err) {
        this.scanError = err.message;
        showErrorAlert('Erro no scan', err.message);
      } finally {
        this.scanning = false;
      }
    },
  },
};
</script>

<style scoped>
.scan-folheto-card {
  border: none;
  border-radius: 1rem;
  max-width: 600px;
  margin: 0 auto;
}

.form-label-custom {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  margin-bottom: 6px;
  display: block;
}

.upload-zone {
  border: 2px dashed #dee2e6;
  border-radius: 0.75rem;
  padding: 2rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
}

.upload-zone:hover,
.upload-zone.drag-over {
  border-color: #17c1e8;
  background: rgba(23, 193, 232, 0.04);
}

.upload-zone.has-file {
  border-color: #20c997;
  border-style: solid;
  background: rgba(32, 201, 151, 0.04);
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.upload-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: linear-gradient(310deg, rgba(23,193,232,0.12), rgba(32,201,151,0.12));
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  font-size: 24px;
  color: #17c1e8;
}

.upload-preview-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 0 8px;
}

.preview-thumb {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #20c997;
  flex-shrink: 0;
}

.preview-meta {
  flex: 1;
  text-align: left;
  overflow: hidden;
}

.btn-remove-file {
  background: none;
  border: none;
  color: #dc3545;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.btn-remove-file:hover {
  opacity: 1;
}

.btn-scan {
  padding: 0.6rem 2rem;
  border-radius: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.btn-custom-gradient {
  background: linear-gradient(310deg, #17c1e8 0%, #20c997 100%);
  border: none;
  color: white;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-custom-gradient:hover:not(:disabled) {
  background: linear-gradient(310deg, #15a8d1 0%, #1bb88a 100%);
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(23, 193, 232, 0.35);
}

.btn-custom-gradient:active {
  transform: translateY(0);
}

.btn-custom-gradient:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.modal-backdrop {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background-color: rgba(0, 0, 0, 0.5) !important;
}

.modal {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.modal-dialog {
  width: 100%;
  margin: 0.5rem auto;
}

@media (max-width: 576px) {
  .modal-dialog {
    max-width: calc(100% - 1rem);
    margin: 0.5rem;
  }
}

.spinner-teal {
  color: #20c997 !important;
}

.supermarket-select-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 280px;
  overflow-y: auto;
}

.supermarket-select-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f7fafc;
  border: 1.5px solid #eef0f4;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
  margin: 0;
}

.supermarket-select-row:hover {
  border-color: rgba(23, 193, 232, 0.4);
  background: rgba(23, 193, 232, 0.04);
}

.supermarket-select-row--selected {
  border-color: #17c1e8;
  background: rgba(23, 193, 232, 0.08);
}

.supermarket-select-checkbox {
  accent-color: #17c1e8;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
}
</style>
